import { el, setText, setStyle, clamp, damp, ease } from './util.js';
import { QUALITY_ORDER } from '../core/config.js';
import { IDLE_KEY, IDLE_MODES } from '../core/idle.js';

const PRESETS = QUALITY_ORDER;

/** Where a manual preset choice is remembered for the next boot (src/main.js). */
export const QUALITY_KEY = 'cod:quality';

/**
 * Pause / settings menu.
 *
 * Wired straight into `ctx.config`: the quality segments call
 * `config.setQuality`, the sliders write `config.sensitivity` and `config.fov`
 * (and push the FOV into the live camera), and every change is announced on the
 * event bus so render/player can react without importing this module.
 *
 * Events emitted: `ui:pause` {paused}, `ui:quality` {quality},
 * `ui:sensitivity` {value}, `ui:fov` {value}, `ui:setting` {key, value}.
 */
export class PauseMenu {
  constructor(parent, ctx) {
    this.ctx = ctx;
    this.root = el('div', 'ow-menu', parent);
    const inner = el('div', 'ow-menu-inner', this.root);

    const h = el('h1', null, inner, 'Paused');
    h.textContent = 'PAUSED';
    el('div', 'sub', inner, 'OVERWATCH — TACTICAL OPERATIONS');
    el('div', 'rule', inner);

    this.rows = el('div', null, inner);

    // ---- quality preset --------------------------------------------------
    this.qBtns = [];
    const qRow = this._row('Graphics Preset');
    const seg = el('div', 'ow-seg', qRow);
    for (const p of PRESETS) {
      const b = el('button', null, seg, p);
      b.type = 'button';
      b.addEventListener('click', () => this.setQuality(p));
      this.qBtns.push(b);
    }
    // Filled in by setQuality; also carries the auto-detected tier on boot so
    // the player can see what the machine was judged to be.
    this.qNote = el('div', 'hint', this.rows, '');

    // ---- sensitivity -----------------------------------------------------
    this.sens = this._slider('Mouse Sensitivity', 0.2, 3.0, 0.01, (v) => {
      this.ctx.config.sensitivity = 0.0022 * v;
      this.ctx.events.emit('ui:sensitivity', { value: this.ctx.config.sensitivity, multiplier: v });
      return v.toFixed(2);
    });

    // ---- field of view ---------------------------------------------------
    this.fov = this._slider('Field Of View', 65, 120, 1, (v) => {
      this.ctx.config.fov = v;
      const cam = this.ctx.camera;
      if (cam) {
        cam.fov = v;
        cam.updateProjectionMatrix();
      }
      this.ctx.events.emit('ui:fov', { value: v });
      return String(v | 0);
    });

    // ---- invert look -----------------------------------------------------
    const invRow = this._row('Invert Look');
    const invSeg = el('div', 'ow-seg', invRow);
    this.invBtns = [];
    for (const [label, val] of [
      ['off', false],
      ['on', true],
    ]) {
      const b = el('button', null, invSeg, label);
      b.type = 'button';
      b.addEventListener('click', () => {
        this.ctx.config.invertY = val;
        this.ctx.events.emit('ui:setting', { key: 'invertY', value: val });
        this.syncFromConfig();
      });
      this.invBtns.push([b, val]);
    }

    // ---- idle behaviour --------------------------------------------------
    // Not a graphics setting and deliberately placed away from the preset row:
    // it decides what happens to frames NOBODY IS LOOKING AT. Nothing here
    // removes an effect or changes a pixel of a frame you can see.
    const idleRow = this._row('When Not Playing');
    const idleSeg = el('div', 'ow-seg', idleRow);
    this.idleBtns = [];
    for (const [label, mode] of [
      ['full', 'off'],
      ['idle', 'safe'],
      ['bg', 'background'],
    ]) {
      const b = el('button', null, idleSeg, label);
      b.type = 'button';
      b.addEventListener('click', () => this.setIdleMode(mode));
      this.idleBtns.push([b, mode]);
    }
    this.idleNote = el('div', 'hint', this.rows, '');

    // ---- buttons ---------------------------------------------------------
    const btns = el('div', 'ow-btns', inner);
    this.resumeBtn = el('button', 'ow-btn primary', btns, 'Resume');
    this.resumeBtn.type = 'button';
    this.resumeBtn.addEventListener('click', () => this.close());
    const reset = el('button', 'ow-btn', btns, 'Defaults');
    reset.type = 'button';
    reset.addEventListener('click', () => {
      this.sens.set(1);
      this.fov.set(80);
      this.ctx.config.invertY = false;
      this.setIdleMode('safe');
      this.setQuality('ultra');
    });
    el('div', 'hint', inner, 'ESC RESUME · WASD MOVE · SHIFT SPRINT · R RELOAD · F USE');

    this.open = false;
    this.shown = 0;
    setStyle(this.root, 'display', 'none');
    setStyle(this.root, 'cursor', 'default');
    this.syncFromConfig();
  }

  _row(name) {
    const r = el('div', 'ow-row', this.rows);
    el('div', 'name', r, name.toUpperCase());
    return r;
  }

  _slider(name, min, max, step, apply) {
    const row = this._row(name);
    const wrap = el('div', 'ow-slider', row);
    el('div', 'track', wrap);
    const fill = el('div', 'fill', wrap);
    const knob = el('div', 'knob', wrap);
    const input = el('input', null, wrap);
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    const val = el('div', 'val', row, '');

    const paint = (v) => {
      const t = (v - min) / (max - min);
      setStyle(fill, 'width', (t * 100).toFixed(2) + '%');
      setStyle(knob, 'left', (t * 100).toFixed(2) + '%');
      setText(val, apply(v) ?? String(v));
    };
    input.addEventListener('input', () => paint(parseFloat(input.value)));
    const api = {
      set: (v) => {
        const c = clamp(v, min, max);
        input.value = String(c);
        paint(c);
      },
    };
    return api;
  }

  /**
   * Switch graphics preset.
   *
   * Only part of a preset can change without a reload, and this used to be
   * silent about it: `config.setQuality` rewrote `config.q`, but the renderer
   * reads most of that once, in `init()`. Cascade count and volumetric step
   * count are baked into compiled shader permutations, and the CSM array
   * texture is allocated up front — so clicking a preset moved nothing the
   * player could see, and the button lit up anyway.
   *
   * Now: the internal resolution (the biggest single lever) is applied live,
   * the choice is persisted so the next boot honours it in full, and the UI
   * says which half you just got.
   */
  setQuality(name) {
    try {
      this.ctx.config.setQuality(name);
      globalThis.localStorage?.setItem(QUALITY_KEY, name);

      const render = this.ctx.peek?.('render');
      if (render) {
        // Restart the governor from the new preset's own scale. Its CEILING is
        // deliberately left alone at 1.0 — a preset's renderScale is a starting
        // point, not a cap, and clamping it here would strand a machine with
        // headroom below native for the rest of the session.
        const snapped = render.governor?.reset(this.ctx.config.q.renderScale);
        render.setRenderScale(snapped ?? this.ctx.config.q.renderScale);
      }
      this.ctx.events.emit('ui:quality', { quality: name });
    } catch (err) {
      console.warn('[ui] quality switch failed', err);
    }
    this.syncFromConfig();
    setText(this.qNote, 'RESOLUTION APPLIED NOW · FULL PRESET ON NEXT LAUNCH');
  }

  /**
   * Pick what happens to unseen frames. Applies instantly — the governor reads
   * `config.idleMode` live, on every frame — and persists like the preset does.
   */
  setIdleMode(mode) {
    if (!IDLE_MODES.includes(mode)) return;
    this.ctx.config.idleMode = mode;
    try {
      globalThis.localStorage?.setItem(IDLE_KEY, mode);
    } catch {
      /* private mode / storage disabled — the choice just won't outlive the tab */
    }
    // Never leave the pipeline parked on a stale decision: 'off' has to start
    // drawing again immediately, and the menu is open over a frozen world.
    this.ctx.engine?.idle?.wake();
    this.ctx.events.emit('ui:setting', { key: 'idleMode', value: mode });
    this.syncFromConfig();
  }

  syncFromConfig() {
    const cfg = this.ctx.config;
    for (let i = 0; i < this.qBtns.length; i++)
      this.qBtns[i].classList.toggle('on', PRESETS[i] === cfg.quality);
    if (this.qNote && !this.qNote.textContent) {
      const g = this.ctx.peek?.('render')?.governor;
      const pct = g ? ` · RENDERING AT ${(g.scale * 100) | 0}%` : '';
      setText(this.qNote, cfg.autoQuality ? `AUTO-DETECTED${pct}` : `MANUAL${pct}`);
    }
    for (const [b, v] of this.invBtns) b.classList.toggle('on', !!cfg.invertY === v);

    const idle = cfg.idleMode ?? 'safe';
    if (this.idleBtns) {
      for (const [b, m] of this.idleBtns) b.classList.toggle('on', idle === m);
      setText(
        this.idleNote,
        {
          off: 'ALWAYS RENDERING · EVEN PAUSED OR HIDDEN',
          safe: 'PAUSED AND HIDDEN FRAMES SKIPPED · NOTHING VISIBLE CHANGES',
          background: 'AS IDLE, PLUS 10 FPS WHILE THIS WINDOW IS NOT FOCUSED',
        }[idle] ?? ''
      );
    }

    this.sens?.set((cfg.sensitivity ?? 0.0022) / 0.0022);
    this.fov?.set(cfg.fov ?? 80);
  }

  toggle() {
    this.open ? this.close() : this.show();
  }

  show() {
    if (this.open) return;
    this.open = true;
    this.syncFromConfig();
    setStyle(this.root, 'display', '');
    document.exitPointerLock?.();
    const t = this.ctx.time;
    if (t) {
      this._prevScale = t.scale;
      t.scale = 0;
    }
    this.ctx.peek('player')?.setControlEnabled?.(false);
    this.ctx.events.emit('ui:pause', { paused: true });
  }

  close() {
    if (!this.open) return;
    this.open = false;
    const t = this.ctx.time;
    if (t) t.scale = this._prevScale ?? 1;
    this.ctx.peek('player')?.setControlEnabled?.(true);
    this.ctx.input?.requestPointerLock?.();
    this.ctx.events.emit('ui:pause', { paused: false });
  }

  /** Driven with unscaled time so the fade still runs while the game is frozen. */
  update(rawDt) {
    this.shown = damp(this.shown, this.open ? 1 : 0, 14, rawDt);
    if (this.shown < 0.004) {
      setStyle(this.root, 'display', 'none');
      setStyle(this.root, 'pointer-events', 'none');
      return;
    }
    setStyle(this.root, 'display', '');
    setStyle(this.root, 'pointer-events', this.open ? 'auto' : 'none');
    setStyle(this.root, 'opacity', ease.outQuad(this.shown).toFixed(3));
  }

  dispose() {
    this.root.remove();
  }
}
